import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import SmartButton from "@mui/icons-material/SmartButton";
import { useCallback, useState, type FC } from "react";
import { isChatChannel, isEmailChannel, type ChannelName } from "../../../../channels/types";
import type { TagGroup } from "../../../../merge-tag/components/types";
import { AddButtonListForm } from "../../components/button/add-button-list-form";
import { AddStylableButtonForm } from "../../components/button/add-styleable-button-form";
import { INSERT_BUTTON_LIST_COMMAND, INSERT_STYLABLE_BUTTON_COMMAND } from "../../types/commands";
import type { ButtonNodeBlock } from "../../types/markdown-node-blocks";
import type { StylableButtonForm } from "../../types/styleable-button";
import type { ToolbarProps } from "./props";
import { ToolbarIconButton } from "./toolbar-icon-button";

type Props = ToolbarProps & {
  filterTags: (x: string) => TagGroup;
  label?: string;
  channel: NonNullable<ChannelName>;
};
export const AddButton: FC<Props> = ({ filterTags, disabled, label, onClick, channel }) => {
  const [showModal, setShowModal] = useState(false);
  const [editor] = useLexicalComposerContext();

  const handleChatSave = useCallback(
    (data: { buttons: ButtonNodeBlock[] }): void => {
      data && editor.dispatchCommand(INSERT_BUTTON_LIST_COMMAND, data);
      setShowModal(false);
      onClick?.();
    },
    [onClick, editor],
  );

  const handleEmailSave = useCallback(
    (data: StylableButtonForm): void => {
      data && editor.dispatchCommand(INSERT_STYLABLE_BUTTON_COMMAND, data);
      setShowModal(false);
      onClick?.();
    },
    [onClick, editor],
  );

  return (
    <>
      <ToolbarIconButton
        data-analytics-id="lexical-toolbar-button"
        disabled={disabled}
        label={label}
        icon={<SmartButton fontSize="medium" />}
        data-testid="toolbar-button-create-button"
        aria-label="create-button"
        tabIndex={-1}
        onClick={() => setShowModal(true)}
      />

      {isEmailChannel(channel) && (
        <AddStylableButtonForm
          open={showModal}
          onCancel={() => {
            setShowModal(false);
            onClick && onClick();
          }}
          initialState={{
            text: "",
            url: "",
            buttonType: "primary_button",
            fullWidth: false,
            format: "left",
          }}
          onConfirm={handleEmailSave}
          filterTags={filterTags}
        />
      )}
      {isChatChannel(channel) && (
        <AddButtonListForm
          open={showModal}
          onCancel={() => {
            setShowModal(false);
            onClick && onClick();
          }}
          onConfirm={handleChatSave}
          disabled={disabled}
          filterTags={filterTags}
        />
      )}
    </>
  );
};
